import { createApp, createVNode, render } from 'vue'
import { isClient } from '@lit-element/utils'
import { destroyObject, proxyExposed } from './utils'
import Component from './contextmenu.vue'

import type { App } from 'vue'
import type {
  ContextmenuConfig,
  ContextmenuInstance,
  ContextmenuOptions,
  Key,
} from './symbol'

export type { ContextmenuConfig, ContextmenuOptions }

export class ContextmenuManager {
  name: string

  private _mountedApp: App<unknown> | null
  private _instance: ContextmenuInstance | null
  private _innerApp: App<unknown> | null
  private _container: HTMLElement | null
  private _pending: Promise<Key[] | null> | null
  private _wrapper: HTMLElement | SVGElement | null
  private _mountedEl: HTMLElement | null

  constructor() {
    this._mountedApp = null
    this._instance = null
    this._innerApp = null
    this._container = null
    this._pending = null
    this._wrapper = null
    this._mountedEl = null
    this.name = 'LtContextmenu'
  }

  open(options: ContextmenuOptions) {
    if (!isClient) {
      return
    }
    const { target = document.body, ...others } = options
    this._pending = this._getInstance()!.openContextmenu(others)
    return this._pending
  }

  destroy() {
    this._mountedEl && this._wrapper?.removeChild(this._mountedEl)
    this._innerApp?.unmount()
    this._container && render(null, this._container)
    destroyObject(this)
  }

  isDestroyed() {
    return false
  }

  install(app: App, options: { property?: string } = {}) {
    const { property } = options

    this._mountedApp = app

    if (property || !app.config.globalProperties.$contextmenu) {
      app.config.globalProperties[property || '$contextmenu'] = this
    }
  }

  private _getInstance() {
    if (!isClient) return

    if (this._pending) {
      let innerApp = this._innerApp
      let container = this._container

      const unmount = () => {
        innerApp?.unmount()
        container && render(null, container)

        innerApp = null
        container = null
      }

      this._pending.finally(unmount)
      this._instance?.handleCancel()
    } else {
      this._innerApp?.unmount()
      this._container && render(null, this._container)
    }

    this._pending = null

    if (!this._mountedApp) {
      // console.warn(
      //   '[Contextmenu]: App missing, the plugin maybe not installed.'
      // )

      this._container = document.createElement('div')
      this._innerApp = createApp(Component)
      this._instance = this._innerApp.mount(
        this._container
      ) as ContextmenuInstance
    } else {
      const vnode = createVNode(Component, null, null)

      this._container = document.createElement('div')
      vnode.appContext = this._mountedApp._context
      render(vnode, this._container)
      this._instance = proxyExposed<ContextmenuInstance>(vnode)
    }
    return this._instance
  }
}

export const Contextmenu = new ContextmenuManager()
